import { parseISO, isWithinInterval } from 'date-fns';
import type { AppAgendaDebate, AppDebate } from '@debatdirect/common/types/debate';
import type { App, AppRecess, AppLocationsDict, AppAgenda } from '@debatdirect/common/types/app';
import type {
  AgendaDebate,
  AgendaResponse,
  AgendaWeeksResponse,
  AppResponse,
  DebateLocation,
  DebateResponse,
  Recess,
} from '@debatdirect/common/types/debatdirect-typings';

import { startDateCompareFn } from './debate';

const determineDebateStatus = ({ startedAt, endedAt }: DebateResponse | AgendaDebate) => {
  if (!startedAt) return 'planned';
  if (!endedAt) return 'live';

  return 'vod';
};

const createLocationDictionary = (locations: DebateLocation[] = []): AppLocationsDict => {
  return Object.fromEntries(Object.entries(locations).map(([_, location]) => [location.id, location]));
};

export const transformAppData = ({ faq, locations, recesses, terminology }: AppResponse): App => ({
  faq,
  locations: createLocationDictionary(locations),
  activeRecess: transformRecessData(recesses),
  terminology,
});

export const transformAgendaData = ({ date, currentDate, debates, documents, categories, overview }: AgendaResponse): AppAgenda => ({
  date: parseISO(date),
  currentDate: parseISO(currentDate),
  debates: debates.map(transformDebateData).sort(startDateCompareFn),
  documents,
  categories,
  overview, // @todo: still used?
});

export const transformDebateData = <T extends DebateResponse | AgendaDebate, R = T extends DebateResponse ? AppDebate : AppAgendaDebate>(debate: T) => {
  return {
    ...debate,
    debateDateStr: debate.debateDate,
    debateDate: parseISO(debate.debateDate),
    startsAt: parseISO(debate.startsAt),
    endsAt: debate.endsAt ? parseISO(debate.endsAt) : null,
    startedAt: debate.startedAt ? parseISO(debate.startedAt) : null,
    endedAt: debate.endedAt ? parseISO(debate.endedAt) : null,
    status: determineDebateStatus(debate),
    // @todo: remove after migration
    rawDates: {
      startsAt: debate.startsAt,
      endsAt: debate.endsAt,
      startedAt: debate.startedAt,
      endedAt: debate.endedAt,
    },
  } as R;
};

/**
 * @deprecated Only used to revert to the legacy API format
 */
export const revertTransformDebateData = <T extends AppDebate | AppAgendaDebate, R = T extends AppDebate ? DebateResponse : AgendaDebate>(debate: T): R => {
  return {
    ...debate,
    debateDate: debate.debateDateStr,
    ...debate.rawDates,
  } as R;
};

const transformRecessData = (recesses: Recess[]) => {
  return recesses?.reduce<AppRecess | undefined>((prev, recess) => {
    const now = new Date();
    const startDate = parseISO(recess.startDate);
    const endDate = parseISO(recess.endDate);

    if (isWithinInterval(now, { start: startDate, end: endDate })) {
      return {
        description: recess.description,
        startDate,
        endDate,
      };
    }
    return prev;
  }, undefined);
};

export const transformAgendaVotingsData = (votings: AgendaWeeksResponse) => {
  const days = votings.debates.reduce(
    (previousValue, currentValue) => {
      previousValue[currentValue.debateDate] ??= { debates: [], date: currentValue.debateDate };
      previousValue[currentValue.debateDate].debates.push(currentValue);

      return previousValue;
    },
    {} as Record<string, Omit<AgendaWeeksResponse, 'currentDate'>>,
  );

  return Object.values(days).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};
